import { useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';

import type { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { ACCESS_TYPES, resolveOrgAccess } from '@src/packages/utils/access';

import {
  buildEmergencyDivisionOptions,
  canCreateEmergencyTypes,
  isManageableDivisionAccess,
  toDivisionMap,
  type EmergencyDivisionOption,
} from '../utils/emergencyAccess';

export type { EmergencyDivisionOption };

/** Stable fallback: a fresh `[]` per render would re-create every subscription. */
const NO_DIVISION_IDS: string[] = [];

interface EmergencyAccessReduxState extends CommonReduxState {
  user: CommonReduxState['user'] & {
    orgUser?: {
      data?: {
        access?: string;
      };
    };
  };
}

interface EmergencyAccess {
  organizationId: string;
  currentUserId: string;
  divisionOptions: EmergencyDivisionOption[];
  accessibleDivisionIds: string[];
  selectedDivisionIds: string[];
  divisionFilterIds: string[] | null;
  isOrganizationAdmin: boolean;
  hasOrganizationWideReadAccess: boolean;
  canManageIntegrations: boolean;
  canCreateTypes: boolean;
  hasCommandCenterAccess: boolean;
  canViewAllInDivision: (divisionId: string) => boolean;
  canManageDivision: (divisionId: string) => boolean;
}

export const useEmergencyAccess = (): EmergencyAccess => {
  const organizationId = useSelector(
    (state: EmergencyAccessReduxState) => state.organizations.organization.id,
  );
  const currentUserId = useSelector(
    (state: EmergencyAccessReduxState) => state.user.profile.data?.id ?? '',
  );
  const claims = useSelector((state: EmergencyAccessReduxState) => state.user.access.data?.claims);
  const orgUserAccess = useSelector(
    (state: EmergencyAccessReduxState) => state.user.orgUser?.data?.access,
  );
  const orgAccess = resolveOrgAccess(claims, organizationId, orgUserAccess);
  const assignedDivisions = useSelector(
    (state: EmergencyAccessReduxState) => state.divisions.assigned.data?.divisions,
  );
  const profileOrgDivisions = useSelector(
    (state: EmergencyAccessReduxState) =>
      state.user.profile.data?.organizations?.[organizationId]?.divisions,
  );
  const selectedIds = useSelector(
    (state: EmergencyAccessReduxState) => state.divisions.selector.ids ?? NO_DIVISION_IDS,
  );

  const isOrganizationAdmin = orgAccess === ACCESS_TYPES.OWNER || orgAccess === ACCESS_TYPES.ADMIN;
  const hasOrganizationWideReadAccess = isOrganizationAdmin || orgAccess === ACCESS_TYPES.READER;
  const assignedDivisionMap = useMemo(() => toDivisionMap(assignedDivisions), [assignedDivisions]);

  const assignedDivisionOptions = useMemo(
    () =>
      buildEmergencyDivisionOptions({
        assignedDivisions: assignedDivisionMap,
        profileOrgDivisions,
        hasOrganizationWideReadAccess,
        isOrganizationAdmin,
      }),
    [
      assignedDivisionMap,
      hasOrganizationWideReadAccess,
      isOrganizationAdmin,
      profileOrgDivisions,
    ],
  );

  const roleReadableDivisionIds = useMemo(
    () =>
      assignedDivisionOptions
        .filter(
          division =>
            hasOrganizationWideReadAccess ||
            isManageableDivisionAccess(division.access) ||
            division.access === ACCESS_TYPES.EDITOR ||
            division.access === ACCESS_TYPES.READER,
        )
        .map(division => division.id),
    [assignedDivisionOptions, hasOrganizationWideReadAccess],
  );

  const accessibleDivisionIds = roleReadableDivisionIds;

  const divisionOptions = useMemo(
    () =>
      accessibleDivisionIds.map(id => {
        const assigned = assignedDivisionOptions.find(division => division.id === id);
        return assigned ?? { id, name: id };
      }),
    [accessibleDivisionIds, assignedDivisionOptions],
  );

  const selectedDivisionIds = useMemo(() => {
    const selectedAccessible = selectedIds.filter(id => accessibleDivisionIds.includes(id));
    return selectedAccessible.length ? selectedAccessible : accessibleDivisionIds;
  }, [accessibleDivisionIds, selectedIds]);

  /**
   * Divisions the view must narrow down to on the client, or `null` for "no
   * restriction".
   *
   * The sidebar division selector is the app-wide scope control, but the
   * org-wide emergency queries deliberately drop `divisionId` (one broad read
   * instead of chunked `in` queries), so an admin kept seeing every division no
   * matter what the selector said. Re-applying the scope here is what makes the
   * selector work again.
   *
   * It only kicks in once the operator has genuinely narrowed the selection.
   * With every division selected this stays `null`, so an org-wide reader keeps
   * seeing incidents from divisions they are not assigned to — that reach is
   * the point of org-wide access, and silently hiding an active emergency to
   * honour a filter nobody touched would be the worse failure.
   */
  const divisionFilterIds = useMemo(
    () =>
      selectedDivisionIds.length < accessibleDivisionIds.length ? selectedDivisionIds : null,
    [accessibleDivisionIds, selectedDivisionIds],
  );

  const canManageDivision = useCallback(
    (divisionId: string) =>
      isOrganizationAdmin || isManageableDivisionAccess(assignedDivisionMap[divisionId]?.access),
    [assignedDivisionMap, isOrganizationAdmin],
  );

  const canViewAllInDivision = useCallback(
    (divisionId: string) => {
      const access = assignedDivisionMap[divisionId]?.access;
      return (
        hasOrganizationWideReadAccess ||
        isManageableDivisionAccess(access) ||
        access === ACCESS_TYPES.EDITOR ||
        access === ACCESS_TYPES.READER
      );
    },
    [assignedDivisionMap, hasOrganizationWideReadAccess],
  );

  const canCreateTypes = canCreateEmergencyTypes({
    isOrganizationAdmin,
    divisionOptions: assignedDivisionOptions,
    canManageDivision,
  });

  return {
    organizationId,
    currentUserId,
    divisionOptions,
    accessibleDivisionIds,
    selectedDivisionIds,
    divisionFilterIds,
    isOrganizationAdmin,
    hasOrganizationWideReadAccess,
    canManageIntegrations: isOrganizationAdmin,
    canCreateTypes,
    hasCommandCenterAccess:
      hasOrganizationWideReadAccess || accessibleDivisionIds.length > 0,
    canViewAllInDivision,
    canManageDivision,
  };
};
